import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, Linkedin, Download, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';

const JordanPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Jordan Sloan - Customer Experience & Outreach | CTC Waterworks"
        description="Connect with Jordan Sloan, Customer Experience & Outreach specialist at CTC Waterworks. Get her contact information and learn about CTC's agricultural water solutions."
        canonicalPath="/team/jordan"
      />
      <Navbar />

      {/* Header Section */}
      <section className="pt-24 pb-8 bg-gradient-to-br from-slate-50 via-ctc-silver-light to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link to="/" className="inline-block mb-6">
              <img
                src="/CTC_Waterworks Logo_Blue_White.png"
                alt="CTC Waterworks"
                className="h-16 w-auto mx-auto"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling;
                  if (fallback) (fallback as HTMLElement).style.display = 'block';
                }}
              />
              <div style={{ display: 'none' }} className="text-3xl font-bold text-ctc-blue">
                CTC Waterworks
              </div>
            </Link>
            <p className="text-ctc-blue font-semibold text-lg mb-8">Better Water = Better Profits</p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-12"
          >
            <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-6 border-4 border-pink-200 shadow-xl">
              <img
                src="/Jordan Sloan.png"
                alt="Jordan Sloan"
                className="w-full h-full object-cover"
              />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
              Jordan Sloan
            </h1>
            <p className="text-xl text-gray-600 font-semibold mb-2">Customer Experience & Outreach</p>
            <p className="text-sm text-gray-500">Tap below to save my contact directly to your phone</p>
          </motion.div>

          {/* Contact Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-4 mb-12"
          >
            <a
              href="/Jordan-Sloan.vcf"
              download
              className="w-full bg-gradient-to-r from-pink-500 to-pink-600 text-white px-8 py-5 rounded-xl font-semibold hover:from-pink-600 hover:to-pink-700 transition-all duration-300 inline-flex items-center justify-center space-x-3 shadow-lg text-lg"
            >
              <Download size={24} />
              <span>Add Jordan to Your Contacts</span>
            </a>

            <a
              href="tel:+15099017476"
              className="w-full bg-white border-2 border-pink-500 text-pink-600 px-8 py-5 rounded-xl font-semibold hover:bg-pink-500 hover:text-white transition-all duration-300 inline-flex items-center justify-center space-x-3 shadow-md text-lg"
            >
              <Phone size={24} />
              <span>Call Jordan</span>
            </a>

            <a
              href="mailto:Jordan@CTCWaterworks.com"
              className="w-full bg-white border-2 border-pink-500 text-pink-600 px-8 py-5 rounded-xl font-semibold hover:bg-pink-500 hover:text-white transition-all duration-300 inline-flex items-center justify-center space-x-3 shadow-md text-lg"
            >
              <Mail size={24} />
              <span>Email Jordan</span>
            </a>

            <a
              href="https://www.linkedin.com/in/jordan-sloan-275a4829b/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-white border-2 border-pink-500 text-pink-600 px-8 py-5 rounded-xl font-semibold hover:bg-pink-500 hover:text-white transition-all duration-300 inline-flex items-center justify-center space-x-3 shadow-md text-lg"
            >
              <Linkedin size={24} />
              <span>Connect on LinkedIn</span>
            </a>
          </motion.div>

          {/* About Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-pink-50 to-pink-100/50 rounded-2xl p-8 border border-pink-200 mb-12"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">About Jordan</h2>
            <p className="text-gray-700 leading-relaxed">
              Jordan serves as a frontline voice for CTC Waterworks, connecting with growers, packers, and operators to understand what's really happening in their operations. She listens closely to customer needs, gathers feedback, and shares those insights with our team so we can continuously improve how we serve the industry. From event follow-up and customer outreach to creating content that tells real grower stories, Jordan helps turn better conversations into better water solutions and new relationships.
            </p>
          </motion.div>

          {/* CTC Links Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-gray-200"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Explore CTC Solutions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/sulfur-burners"
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg border border-gray-200 hover:border-pink-500 transition-all duration-300 group"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-pink-600 transition-colors">
                  Sulfur Burners
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Improve water quality and soil pH management
                </p>
                <div className="flex items-center text-pink-600 text-sm font-medium">
                  <span>Learn More</span>
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                </div>
              </Link>

              <Link
                to="/hydrocoolers"
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg border border-gray-200 hover:border-pink-500 transition-all duration-300 group"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-pink-600 transition-colors">
                  Hydrocoolers
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Preserve freshness and extend shelf life
                </p>
                <div className="flex items-center text-pink-600 text-sm font-medium">
                  <span>Learn More</span>
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                </div>
              </Link>

              <Link
                to="/filtration"
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg border border-gray-200 hover:border-pink-500 transition-all duration-300 group"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-pink-600 transition-colors">
                  Filtration Systems
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Protect irrigation systems and equipment
                </p>
                <div className="flex items-center text-pink-600 text-sm font-medium">
                  <span>Learn More</span>
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                </div>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JordanPage;
